import OpenAI from 'openai';

/**
 * Enhanced OpenAI client validation with comprehensive authentication testing
 * @returns {Object} Validation results with detailed status information
 */
const validateOpenAIConfiguration = () => {
  const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
  
  const validation = {
    hasApiKey: !!apiKey,
    keyLength: apiKey?.length || 0,
    keyPrefix: apiKey?.substring(0, 8) || 'Not found',
    isPlaceholder: false,
    isValidFormat: false,
    environment: import.meta.env?.MODE || 'unknown',
    errors: [],
    warnings: []
  };

  // Enhanced placeholder detection with more patterns
  const placeholders = [
    'your-openai-api-key-here', 
    'undefined', 
    '', 
    'null',
    'your-api-key-here',
    'sk-your-api-key-here',
    'VITE_OPENAI_API_KEY',
    'sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'
  ];
  
  validation.isPlaceholder = !apiKey || 
    placeholders?.includes(apiKey) || 
    apiKey?.trim()?.length === 0 ||
    apiKey?.includes('your-') ||
    apiKey?.includes('xxxxx');

  if (validation?.isPlaceholder) {
    validation?.errors?.push('OpenAI API key is a placeholder or not configured - using fallback responses');
  }

  // Enhanced format validation with specific checks
  if (apiKey && !validation?.isPlaceholder) {
    if (apiKey?.startsWith('sk-')) {
      validation.isValidFormat = true;
      
      // Additional format checks for newer key formats
      if (apiKey?.length < 50) {
        validation?.warnings?.push('API key appears to be too short for standard OpenAI format');
      } else if (apiKey?.length > 200) {
        validation?.warnings?.push('API key appears unusually long');
      }
      
      // Check for common key format patterns
      if (apiKey?.includes(' ') || apiKey?.includes('\n') || apiKey?.includes('\t')) {
        validation?.errors?.push('API key contains whitespace characters - please check formatting');
      }
    } else {
      validation?.errors?.push('API key format invalid (must start with "sk-")');
    }
  }

  // Check for environment variable loading issues
  if (!import.meta.env?.VITE_OPENAI_API_KEY && process?.env?.VITE_OPENAI_API_KEY) {
    validation?.warnings?.push('API key found in process.env but not in import.meta.env - Vite prefix issue?');
  }

  // Overall validity with enhanced checks
  validation.isValid = validation?.hasApiKey && 
                      !validation?.isPlaceholder && 
                      validation?.isValidFormat && 
                      validation?.errors?.length === 0;

  return validation;
};

/**
 * Enhanced OpenAI client initialization with immediate authentication verification
 * @returns {OpenAI|null} Configured OpenAI client instance or null if invalid
 */
const initializeOpenAIClient = () => {
  const validation = validateOpenAIConfiguration();

  if (!validation?.isValid) {
    if (import.meta.env?.MODE === 'development') {
      console.group('🔧 OpenAI Configuration Diagnostics:');
      console.log('Environment Mode:', validation?.environment);
      console.log('API Key Present:', validation?.hasApiKey);
      console.log('Key Format Valid:', validation?.isValidFormat);
      console.log('Is Placeholder:', validation?.isPlaceholder);
      
      if (validation?.errors?.length > 0) {
        console.error('Configuration Errors:');
        validation?.errors?.forEach(error => console.error(`❌ ${error}`));
      }
      
      if (validation?.warnings?.length > 0) {
        console.warn('Configuration Warnings:');
        validation?.warnings?.forEach(warning => console.warn(`⚠️ ${warning}`));
      }
      
      console.log('💡 Quick Setup Guide:');
      console.log('1. Get API key from https://platform.openai.com/api-keys');
      console.log('2. Add VITE_OPENAI_API_KEY=your-key to .env file');
      console.log('3. Restart development server');
      console.groupEnd();
    }
    
    return null;
  }

  try {
    // Enhanced configuration with optional organization and project settings
    const clientConfig = {
      apiKey: import.meta.env?.VITE_OPENAI_API_KEY,
      dangerouslyAllowBrowser: true,
      timeout: 15000, // 15 second timeout for better error handling
    };

    // Add optional organization ID if provided
    if (import.meta.env?.VITE_OPENAI_ORGANIZATION_ID && 
        import.meta.env?.VITE_OPENAI_ORGANIZATION_ID !== 'your-openai-organization-id-here') {
      clientConfig.organization = import.meta.env?.VITE_OPENAI_ORGANIZATION_ID;
    }

    // Add optional project ID if provided
    if (import.meta.env?.VITE_OPENAI_PROJECT_ID && 
        import.meta.env?.VITE_OPENAI_PROJECT_ID !== 'your-openai-project-id-here') {
      clientConfig.project = import.meta.env?.VITE_OPENAI_PROJECT_ID;
    }

    const actualClient = new OpenAI(clientConfig);

    // Success logging in development
    if (import.meta.env?.MODE === 'development') {
      console.log('✅ OpenAI client initialized successfully');
      if (clientConfig?.organization) {
        console.log('🏢 Organization ID configured:', clientConfig?.organization?.substring(0, 8) + '...');
      }
      if (clientConfig?.project) {
        console.log('📁 Project ID configured:', clientConfig?.project?.substring(0, 8) + '...');
      }
    }

    return actualClient;
  } catch (error) {
    console.error('❌ OpenAI client initialization failed:', error?.message);
    console.warn('🔄 Fallback responses will be used for AI features');
    return null;
  }
};

// Initialize the client
const openai = initializeOpenAIClient();

// Export with additional validation
export default openai;

/**
 * Enhanced validation function for OpenAI client readiness with real-time checks
 * @returns {boolean} True if client is ready for use
 */
export const isOpenAIConfigured = () => {
  const validation = validateOpenAIConfiguration();
  return validation?.isValid && !!openai;
};

/**
 * Get configured model or default
 * @returns {string} Model name to use for API calls
 */
export const getConfiguredModel = () => {
  const modelOverride = import.meta.env?.VITE_OPENAI_MODEL_OVERRIDE;
  if (modelOverride && modelOverride !== 'gpt-4o-mini') {
    return modelOverride;
  }
  return 'gpt-4o-mini';
};

/**
 * Comprehensive configuration status for advanced diagnostics
 * @returns {Object} Detailed configuration information with troubleshooting
 */
export const getConfigStatus = () => {
  const validation = validateOpenAIConfiguration();
  
  const status = {
    ...validation,
    clientReady: !!openai,
    status: validation?.isValid ? 'ready' : 'configuration_required',
    modelOverride: getConfiguredModel(),
    organizationConfigured: !!(import.meta.env?.VITE_OPENAI_ORGANIZATION_ID && 
      import.meta.env?.VITE_OPENAI_ORGANIZATION_ID !== 'your-openai-organization-id-here'),
    projectConfigured: !!(import.meta.env?.VITE_OPENAI_PROJECT_ID && 
      import.meta.env?.VITE_OPENAI_PROJECT_ID !== 'your-openai-project-id-here'),
    environmentInfo: {
      mode: import.meta.env?.MODE,
      hasVitePrefix: !!import.meta.env?.VITE_OPENAI_API_KEY,
      keySource: import.meta.env?.VITE_OPENAI_API_KEY ? 'import.meta.env' : 'not_found'
    },
    troubleshooting: {
      commonIssues: [
        'API key not set in .env file',
        'Missing VITE_ prefix in environment variable',
        'Development server not restarted after adding key',
        'API key copied incorrectly (whitespace/formatting)',
        'Using an expired or revoked API key'
      ],
      quickFixes: [
        'Check .env file exists and has VITE_OPENAI_API_KEY=sk-...',
        'Restart development server (npm run dev)',
        'Verify API key at https://platform.openai.com/api-keys',
        'Check billing status at https://platform.openai.com/account/billing'
      ]
    }
  };

  if (!validation?.isValid) {
    status.quickSetup = [
      '1. Get API key from https://platform.openai.com/api-keys',
      '2. Add VITE_OPENAI_API_KEY=your-key to .env file',
      '3. Restart development server',
      '4. Test connection using diagnostics button'
    ];
    
    status.setupInstructions = [
      'Create or edit .env file in your project root',
      'Add the line: VITE_OPENAI_API_KEY=sk-your-actual-key-here',
      'Save the file and restart your development server',
      'The key should start with "sk-" and be around 50+ characters'
    ];
  }

  return status;
};

/**
 * Enhanced API key authentication test with comprehensive error analysis
 * @returns {Promise<Object>} Test result with detailed authentication status
 */
export const testAuthentication = async () => {
  const validation = validateOpenAIConfiguration();
  
  if (!validation?.isValid || !openai) {
    return {
      success: false,
      error: 'configuration_invalid',
      message: 'OpenAI API key not configured properly',
      quickFix: 'Add VITE_OPENAI_API_KEY to your .env file',
      details: validation,
      suggestions: [
        'Verify VITE_OPENAI_API_KEY is set in .env file',
        'Ensure API key starts with "sk-"',
        'Restart development server after adding the key',
        'Check for typos or extra spaces in the API key'
      ]
    };
  }

  try {
    console.log('🔍 Testing OpenAI API authentication...');
    
    // Enhanced authentication test with timeout and better error handling
    const response = await Promise.race([
      openai?.models?.list(),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Authentication test timeout - check network connection')), 8000)
      )
    ]);
    
    const testResult = {
      success: true,
      message: 'OpenAI API authentication successful',
      modelsAvailable: response?.data?.length || 0,
      testTimestamp: new Date()?.toISOString(),
      apiKeyPrefix: validation?.keyPrefix,
      connectionTime: 'Under 8 seconds'
    };
    
    if (import.meta.env?.MODE === 'development') {
      console.log('✅ Authentication test passed:', testResult);
    }
    
    return testResult;
  } catch (error) {
    console.error('❌ OpenAI Authentication Test Failed:', error?.message);
    
    // Enhanced error categorization with specific solutions
    let errorType = 'unknown';
    let userMessage = 'Authentication test failed';
    let quickFix = '';
    let suggestions = [];

    if (error?.status === 401) {
      errorType = 'invalid_api_key';
      userMessage = 'API key is invalid, expired, or revoked';
      quickFix = 'Verify your API key at https://platform.openai.com/api-keys';
      suggestions = [
        'Check if API key was copied correctly',
        'Verify the key hasn\'t expired or been revoked',
        'Try regenerating a new API key',
        'Ensure you have sufficient credits/billing setup'
      ];
    } else if (error?.status === 429) {
      errorType = 'quota_exceeded';
      userMessage = 'API quota exceeded or rate limit reached';
      quickFix = 'Check billing and usage at https://platform.openai.com/account/billing';
      suggestions = [
        'Check your current usage and billing status',
        'Upgrade your OpenAI plan if needed',
        'Wait a few minutes before retrying',
        'Implement request rate limiting in your application'
      ];
    } else if (error?.message?.includes('timeout')) {
      errorType = 'network_timeout';
      userMessage = 'Connection timeout - check internet connection';
      quickFix = 'Check your internet connection and try again';
      suggestions = [
        'Verify your internet connection is stable',
        'Check if firewall is blocking OpenAI API',
        'Try using a different network',
        'Check if OpenAI services are experiencing outages'
      ];
    } else if (error?.status >= 500) {
      errorType = 'server_error';
      userMessage = 'OpenAI servers temporarily unavailable';
      quickFix = 'Wait and try again - this is temporary';
      suggestions = [
        'OpenAI servers may be experiencing issues',
        'Try again in a few minutes',
        'Check OpenAI status page for service updates',
        'Consider implementing retry logic with backoff'
      ];
    } else if (error?.message?.includes('network') || error?.name === 'NetworkError') {
      errorType = 'network_error';
      userMessage = 'Network error preventing API connection';
      quickFix = 'Check network settings and firewall configuration';
      suggestions = [
        'Verify internet connection is working',
        'Check if corporate firewall blocks OpenAI API',
        'Try disabling VPN if using one',
        'Test from a different network'
      ];
    }

    return {
      success: false,
      error: errorType,
      message: userMessage,
      quickFix,
      suggestions,
      status: error?.status,
      fallbackActive: true,
      technicalDetails: {
        errorMessage: error?.message,
        errorName: error?.name,
        stack: error?.stack?.split('\n')?.[0] // First line of stack for debugging
      }
    };
  }
};

/**
 * Enhanced setup guidance with step-by-step troubleshooting
 * @returns {Object} Comprehensive setup guidance with troubleshooting
 */
export const getSetupGuide = () => {
  const validation = validateOpenAIConfiguration();
  
  const guide = {
    currentStatus: validation?.isValid ? 'configured' : 'needs_setup',
    quickSteps: [],
    links: {
      getApiKey: 'https://platform.openai.com/api-keys',
      billing: 'https://platform.openai.com/account/billing',
      documentation: 'https://platform.openai.com/docs/quickstart',
      status: 'https://status.openai.com/'
    },
    troubleshooting: {
      environmentIssues: [
        'API key not in .env file',
        'Missing VITE_ prefix',
        'Server not restarted after changes'
      ],
      keyIssues: [
        'Invalid key format (must start with sk-)',
        'Expired or revoked key',
        'Key copied with extra characters/spaces'
      ],
      networkIssues: [
        'Firewall blocking OpenAI API',
        'Network connectivity problems',
        'Corporate proxy restrictions'
      ]
    }
  };

  // Build step-by-step guide based on current configuration state
  if (!validation?.hasApiKey || validation?.isPlaceholder) {
    guide?.quickSteps?.push({
      step: 1,
      title: 'Get OpenAI API Key',
      action: 'Visit https://platform.openai.com/api-keys',
      status: 'required',
      description: 'Create account and generate new API key'
    });
  }

  if (!validation?.isValidFormat) {
    guide?.quickSteps?.push({
      step: 2,
      title: 'Fix API Key Format',
      action: 'Ensure key starts with "sk-" and is complete',
      status: 'required',
      description: 'Valid keys are ~50+ characters starting with "sk-"'
    });
  }

  guide?.quickSteps?.push({
    step: 3,
    title: 'Add to Environment File',
    action: 'VITE_OPENAI_API_KEY=your-key in .env file',
    status: validation?.isValid ? 'completed' : 'required',
    description: 'Create .env file in project root with your API key'
  });

  guide?.quickSteps?.push({
    step: 4,
    title: 'Restart Development Server',
    action: 'Stop and restart with npm run dev',
    status: 'required',
    description: 'Environment changes require server restart'
  });

  guide?.quickSteps?.push({
    step: 5,
    title: 'Test Configuration',
    action: 'Use diagnostics button to verify setup',
    status: validation?.isValid ? 'completed' : 'pending',
    description: 'Run authentication test to confirm working setup'
  });

  return guide;
};

/**
 * Advanced diagnostic tool for OpenAI configuration issues
 * @returns {Object} Comprehensive diagnostic information
 */
export const runConfigurationDiagnostics = () => {
  const validation = validateOpenAIConfiguration();
  const configStatus = getConfigStatus();
  
  const diagnostics = {
    timestamp: new Date()?.toISOString(),
    environment: {
      mode: import.meta.env?.MODE,
      nodeEnv: process?.env?.NODE_ENV,
      viteVersion: 'Latest' // Could be enhanced to detect actual version
    },
    configuration: validation,
    client: {
      initialized: !!openai,
      ready: isOpenAIConfigured()
    },
    recommendations: []
  };

  // Generate specific recommendations based on findings
  if (!validation?.hasApiKey) {
    diagnostics?.recommendations?.push({
      priority: 'high',
      issue: 'No API key found',
      solution: 'Add VITE_OPENAI_API_KEY to .env file',
      action: 'Get key from https://platform.openai.com/api-keys'
    });
  }

  if (validation?.isPlaceholder) {
    diagnostics?.recommendations?.push({
      priority: 'high',
      issue: 'Placeholder API key detected',
      solution: 'Replace with actual OpenAI API key',
      action: 'Copy real key from OpenAI dashboard'
    });
  }

  if (!validation?.isValidFormat) {
    diagnostics?.recommendations?.push({
      priority: 'medium',
      issue: 'Invalid API key format',
      solution: 'Ensure key starts with "sk-" and is complete',
      action: 'Check for copy/paste errors or truncation'
    });
  }

  if (validation?.warnings?.length > 0) {
    validation?.warnings?.forEach(warning => {
      diagnostics?.recommendations?.push({
        priority: 'low',
        issue: warning,
        solution: 'Review key format and environment setup',
        action: 'Check for whitespace or formatting issues'
      });
    });
  }

  return diagnostics;
};

// Export enhanced diagnostic functions
export { validateOpenAIConfiguration };